/* eslint no-unsafe-optional-chaining: 0 */
import React, { useState, useLayoutEffect } from 'react'
import { number, oneOfType, shape, string } from 'prop-types'
import { css } from '@emotion/core'
import { useContainerWidth } from '@fs/zion-ui'

import HeadingWithIcon from './TileContent/HeadingWithIcon'
import QuizComparison from './TileContent/Quiz/QuizComparison'
import EventComparison from './TileContent/Event/EventComparison'
import LineChartComparison from './TileContent/LineChart/LineChartComparison'
import NameMeaningComparison from './TileContent/NameMeaning/NameMeaningComparison'
import GlobalNameCountsComparison from './TileContent/GlobalNameCounts/GlobalNameCountsComparison'
import NameHeatmapComparison from './TileContent/NameHeatmap/NameHeatmapComparison'
import TempleDedications from './TileContent/TempleDedications/TempleDedications'
import { useTileContentData, useInnerTileVisuals } from '../hooks/hooks'
import { deconstructName, getDisplayName, checkAsianChar } from '../../../shared/helpers/name_formatting'
import AncestorSlider from './AncestorSlider/AncestorSlider'
import PageLoadingIndicator from '../../../shared/components/PageLoadingIndicator'
import AddTreeFlow from '../../../shared/components/AddTreeFlow'
import { useAamState, useAamDispatch } from '../state/AamStore'
import { formatTileContentData } from '../services/services'

export default function TileContent({ tileData, treeDataState, highlightColor }) {
  const [isATFShowing, setIsATFShowing] = useState(false)
  const [tileContentData, tileContentDispatch] = useTileContentData({ tileData })
  const [tileComparisonContent, tileComparisonContentDispatch, gettileComparisonContent] = useTileContentData({
    tileData,
    autoLoad: false,
  })

  const [tileContentProps, setTileContentProps] = useState({ ...tileData })
  const atWidth = useContainerWidth()
  const components = {
    quiz: QuizComparison,
    event: EventComparison,
    lineChart: LineChartComparison,
    nameMeaning: NameMeaningComparison,
    globalNameCounts: GlobalNameCountsComparison,
    nameHeatmap: NameHeatmapComparison,
    templeDedications: TempleDedications,
  }

  const SpecificTileContent = components[tileData.type]
  const tileVisuals = useInnerTileVisuals(tileContentProps?.imageUrl, highlightColor)
  const aamState = useAamState()
  const { isAccessible, showNamesInRomanScript } = aamState
  const aamDispatch = useAamDispatch()

  useLayoutEffect(() => {
    if (isAccessible) {
      const overlayHeader = document.querySelector('[portal="overlays"]')?.querySelector('header')
      overlayHeader.style.position = 'absolute'
      overlayHeader.style.bottom = '165px'
    }
  }, [isAccessible])

  const updateTileContent = (event, relation) => {
    const { dob } = relation
    const { region, id } = tileData
    const formattedDisplayName = getDisplayName(relation, aamState.showNamesInRomanScript)
    const isAsian = !!(checkAsianChar(relation?.name) || checkAsianChar(relation?.surname))
    const comparisonParsedNames = deconstructName(
      formattedDisplayName,
      relation?.name,
      relation?.surname,
      isAsian,
      showNamesInRomanScript
    )
    const { name, isFirst } = formatTileContentData({ ...tileData, parsedNames: comparisonParsedNames })?.nameArgs
    const tileProps = {
      ...tileData,
      comparisonParsedNames,
      countryCode: region,
      comparisonYear: dob ? Number.parseInt(dob, 10) : 0,
      tileId: id,
      comparisonTargetPerson: relation,
      comparisonTargetNameArgs: { name, isFirst },
      portraitThumbnailLink: treeDataState?.res?.[0]?.portraitThumbnailLink,
    }

    const { countryCode, comparisonYear, tileId, comparisonTargetNameArgs } = tileProps

    gettileComparisonContent({
      countryCode,
      year: comparisonYear,
      tileId,
      type: tileData?.type,
      nameArgs: comparisonTargetNameArgs,
      dataUrl: tileData?.dataUrl ? tileData?.dataUrl.replace(/[0-9]{4}/, comparisonYear) : undefined,
    })

    setTileContentProps(tileProps)
  }

  const toggleAtf = () => {
    setIsATFShowing(!isATFShowing)
  }

  const tileContentStyle = css`
    height: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
    background-image: url(${tileVisuals.background});
    background-size: cover;
    background-position: center;
    overflow: hidden;
  `
  const innerTileContentStyle = css`
    align-items: center;
    flex-direction: column;
    flex-grow: 1;
    overflow-y: auto;
    width: 100%;
    padding-top: ${isAccessible ? '220px' : atWidth({ default: '10px', lg: '20px' })};
  `

  return !isATFShowing ? (
    <div css={tileContentStyle}>
      <AncestorSlider
        updateTileContent={updateTileContent}
        tileData={tileData}
        toggleAtf={toggleAtf}
        treeDataState={treeDataState}
      />
      <div css={innerTileContentStyle}>
        {tileContentData?.load || tileComparisonContent?.load ? (
          <PageLoadingIndicator constrain />
        ) : (
          <SpecificTileContent
            highlightColor={highlightColor}
            tileData={tileData}
            tileContentData={tileContentData?.res ?? null}
            tileContentDispatch={tileContentDispatch}
            tileComparisonContent={tileComparisonContent?.res ?? null}
            tileComparisonContentDispatch={tileComparisonContentDispatch}
            headingWithIcon={<HeadingWithIcon headerIcon={tileVisuals?.icon} txt={tileData?.header} />}
            {...tileContentProps}
          />
        )}
      </div>
    </div>
  ) : (
    <AddTreeFlow
      toggleAtf={toggleAtf}
      sourceThatInitiated="All About Me"
      aamState={aamState}
      aamDispatch={aamDispatch}
    />
  )
}

TileContent.propTypes = {
  tileData: shape({
    additionalText: string,
    header: string,
    icon: string,
    id: number,
    img: string,
    language: string,
    parsedNames: shape({}),
    region: string,
    title: string,
    type: string,
    year: oneOfType([string, number]),
  }),
  highlightColor: string,
}
